import { useEffect, useState } from 'react'
import { OpenAcademicTranscript } from '@/components/buttons/open-academic-transcript'
import { RequestActionButton } from '@/components/buttons/request-action-button'
import { RequestData } from '@/components/cards/request-data'
import { RequestDataWorkloadSummary } from '@/components/cards/request-data/workload-summary'
import { ViewStudentObservation } from '@/components/cards/view-student-observation'
import { GrantReviewModal } from '@/components/dialogs/grant-review-modal'
import { NotGrantReviewModal } from '@/components/dialogs/not-grant-review-modal'
import { PageContent } from '@/components/layout/page-content'
import { PageHeader } from '@/components/layout/page-header'
import { PageLayout } from '@/components/layout/page-layout'
import { Show } from '@/components/show'
import { CoursesEquivalences } from '@/components/tables/courses-quivalences'
import { CoursesEquivalencesWorkloadFooter } from '@/components/tables/courses-quivalences/components/workload-footer'
import { SimulatedTranscript } from '@/components/tables/simulated-transcript'
import { RequestStatus } from '@/components/titles/request-status'
import { RequestTitle } from '@/components/titles/request-title'
import { Button } from '@/components/ui/button'
import { canManagerPerformReviewActions } from '@/domain/review/manager-review-action.rules'
import { useReviewDecision } from '@/hooks/domain/review/use-review-decision'
import { useEquivalenceStore } from '@/store/equivalence-store'
import { toastService } from '@/toasts/toastService'
import type { Curriculum } from '@/types/courses/curriculum'
import { formatRequestProtocol } from '@/utils/format-request-protocol'
import { getRequestStatusById } from '@/utils/get-status-by-id'
import { ButtonFooter } from './components/button-footer'
import { useExternalTransferReviewForms } from './hooks/use-external-transfer-review-forms'
import { useExternalTransferReviewQuery } from './hooks/use-external-transfer-review-query'

export const ExternalTransferReviewPage = () => {
  const equivalenceStore = useEquivalenceStore.getState()
  const [isOpenGrantDialog, setIsOpenGrantDialog] = useState(false)
  const [isOpenNotGrantDialog, setIsOpenNotGrantDialog] = useState(false)
  const [currentCurriculum, setCurrentCurriculum] = useState<Curriculum>()

  const {
    states: {
      allCurriculums,
      curriculumCourses,
      previousCourses,
      request,
      allQueriesLoaded,
      isPendingCurriculumCourses
    },
    actions: { refetchRequest }
  } = useExternalTransferReviewQuery({ currentCurriculum })

  const {
    states: { formMethods },
    actions: { clearDesiredCourses, handleSubmit, triggerFormValidations }
  } = useExternalTransferReviewForms(request)

  const {
    states: {
      denyReviewIsPending,
      denyReviewSuccess,
      grantReviewIsPending,
      grantReviewSuccess
    },
    actions: { grantReviewMutate, denyReviewMutate }
  } = useReviewDecision({
    requestId: request?.id ?? '',
    onDecision: () => refetchRequest()
  })

  const handleGrantSubmit = handleSubmit((data) => {
    return grantReviewMutate(data)
  })

  const handleSubmitDeny = () => {
    const observation = formMethods.getValues?.('observation')
    if (observation && observation.trim() !== '') {
      return denyReviewMutate(observation)
    }
  }

  async function openGrantReviewModal(validForm?: boolean) {
    if (validForm) {
      setIsOpenGrantDialog(true)
      return
    }
    await triggerFormValidations()
    toastService.invalidFieldsInGrantReview(formMethods.errors)
  }

  function openDenyReviewModal() {
    setIsOpenNotGrantDialog(true)
  }

  const handleCurrentCurriculumChange = (curriculum: Curriculum) => {
    setCurrentCurriculum(curriculum)

    const storedEquivalence = equivalenceStore.getRecord(
      request?.id || '',
      curriculum.id
    )

    if (!storedEquivalence) {
      clearDesiredCourses()
    } else {
      formMethods?.setValue?.(
        'courseEquivalences',
        storedEquivalence.courseEquivalences
      )
    }
  }

  useEffect(() => {
    if (allCurriculums && request) {
      const current = allCurriculums.find(
        (curriculum) => curriculum.id === request.desiredEducation.curriculum
      )
      setCurrentCurriculum(current)
    }
  }, [allCurriculums, request])

  const status = getRequestStatusById(request?.requestStatus.id)

  const isRefetching = isPendingCurriculumCourses

  const disableActions = !canManagerPerformReviewActions(
    request?.requestStatus.id ?? ''
  )

  return (
    <PageLayout
      isLoading={!allQueriesLoaded || isRefetching}
      loadingText="Carregando Análise"
      onlyFirstLoad={false}
    >
      <PageHeader title="Minhas Análises" breadcrumb>
        <Button disabled className="w-38 bg-[var(--brand-green)] select-none">
          Iniciar Análise
        </Button>
      </PageHeader>

      <PageContent>
        <div className="w-full mb-6 flex justify-between">
          <div className="flex gap-5 items-center">
            <div className="flex items-center gap-2">
              <RequestTitle title="Análise" />
              <RequestTitle
                title={`#${formatRequestProtocol(request?.protocol)}`}
              />
            </div>
            <RequestStatus name={status?.name} color={status?.color} />
          </div>
          <div className="flex items-center gap-10">
            <div>
              <OpenAcademicTranscript
                link={request?.previousEducation?.academicTranscript || '#'}
              />
            </div>
            <RequestActionButton
              requestId=""
              buttonDisabled={disableActions}
              grantActive={true}
              denyActive={true}
              denyAction={openDenyReviewModal}
              grantAction={openGrantReviewModal}
              formMethods={formMethods}
            />
          </div>
        </div>
        <RequestData
          createdAt={request?.createdAt}
          desiredCourse={request?.desiredEducation?.desiredEducation}
          name={request?.student?.person?.name}
          previousCourse={request?.previousEducation?.name}
          ra={request?.student?.ra}
          status={request?.requestStatus?.name}
          workloadSummary={
            <RequestDataWorkloadSummary
              previousEducation={request?.previousEducation}
              curriculumCourses={curriculumCourses}
            />
          }
          curriculoDeIngresso={request?.student.curriculum}
        />

        <Show when={!!request?.observation}>
          <div className="mt-10">
            <ViewStudentObservation
              requestStatusId={request?.requestStatus.id}
              observation={request?.observation}
            />
          </div>
        </Show>

        <div className="mt-10">
          <CoursesEquivalences
            currentCurriculum={currentCurriculum}
            curriculumCourses={curriculumCourses}
            curriculums={allCurriculums}
            setCurrentCurriculum={handleCurrentCurriculumChange}
            request={request}
            formMethods={formMethods}
            previousCourses={previousCourses}
            onlyView={disableActions}
            footer={
              <CoursesEquivalencesWorkloadFooter
                formMethods={formMethods}
                curriculumCourses={curriculumCourses}
              />
            }
          />
        </div>

        <div className="w-full mt-12 mb-6">
          <RequestTitle title="Simulação do Novo Histórico do Aluno" />
        </div>

        <SimulatedTranscript
          subTitle={request?.desiredEducation?.desiredEducation}
          courses={curriculumCourses}
          formMethods={formMethods}
          requestStatus={request?.requestStatus.id}
        />

        <ButtonFooter
          disableButons={disableActions}
          className="mt-12"
          formMethods={formMethods}
          grantAction={openGrantReviewModal}
          denyAction={openDenyReviewModal}
        />

        <GrantReviewModal
          formMethods={formMethods}
          isOpen={isOpenGrantDialog && !grantReviewSuccess}
          setOpen={setIsOpenGrantDialog}
          grantIsPending={grantReviewIsPending}
          grantSubmit={handleGrantSubmit}
        />

        <NotGrantReviewModal
          formMethods={formMethods}
          isOpen={isOpenNotGrantDialog && !denyReviewSuccess}
          setOpen={setIsOpenNotGrantDialog}
          notGrantIsPending={denyReviewIsPending}
          notGrantSubmit={handleSubmitDeny}
        />
      </PageContent>
    </PageLayout>
  )
}
